// AddCategoryModal.tsx
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { CategoryOption } from '../../lib/categories/categories';
import { AccountType, NWCRole, EBITDARole } from '../../types';
import NWCRoleSelector from './NWCRoleSelector';
import EBITDARoleSelector from './EBITDARoleSelector';

interface AddCategoryModalProps {
  onClose: () => void;
  onAdd: (category: CategoryOption) => void;
  accountTypes: AccountType[];
  getFSType: (type: AccountType) => 'balance-sheet' | 'income-statement';
  detectNWCRole?: (category: CategoryOption) => NWCRole | null;
  detectEBITDARole?: (category: CategoryOption) => EBITDARole | null;
}

const AddCategoryModal: React.FC<AddCategoryModalProps> = ({
  onClose,
  onAdd,
  accountTypes,
  getFSType,
  detectNWCRole,
  detectEBITDARole
}) => {
  const [newCategory, setNewCategory] = useState<CategoryOption>({
    value: '',
    label: '',
    type: 'current-asset', // Default to a more common type than n/a
    fsType: 'balance-sheet',
    tagsInclude: [],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  });

  const [nwcRole, setNwcRole] = useState<NWCRole | null>(null);
  const [ebitdaRole, setEbitdaRole] = useState<EBITDARole | null>(null);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newCategory.label.trim()) {
      setError('Category name is required');
      return;
    }

    // Create the category with proper formatting
    const formattedCategory: CategoryOption = {
      ...newCategory,
      value: newCategory.label.toLowerCase().replace(/\s+/g, '-'),
      fsType: getFSType(newCategory.type),
      nwcRole: nwcRole,
      ebitdaRole: ebitdaRole,
      isNonCash: newCategory.isNonCash,
      isPpe: newCategory.isPpe,
      isDebt: newCategory.isDebt,
      isEquityCapital: newCategory.isEquityCapital
    };

    onAdd(formattedCategory);
  };

  const handleTypeChange = (type: AccountType) => {
    const fsType = getFSType(type);
    const updatedCategory = {
      ...newCategory,
      type,
      fsType
    };
    setNewCategory(updatedCategory);
    
    // Auto-detect roles when type changes
    const detectedNWCRole = detectNWCRole?.(updatedCategory) || null;
    const detectedEBITDARole = detectEBITDARole?.(updatedCategory) || null;
    
    setNwcRole(detectedNWCRole);
    setEbitdaRole(detectedEBITDARole);
  };

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Close modal when clicking outside the content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleOutsideClick}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Add New Category</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          <div className="space-y-4">
            <div>
              <label htmlFor="categoryName" className="block text-sm font-medium text-gray-700 mb-1">
                Category Name
              </label>
              <input
                id="categoryName"
                type="text"
                value={newCategory.label}
                onChange={(e) => {
                  setNewCategory({ ...newCategory, label: e.target.value });
                  if (error) setError(''); // Clear error when user types
                }}
                className={`w-full rounded-md border shadow-sm px-4 py-2 focus:outline-none focus:ring-1 ${
                  error 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="Enter category name"
                aria-invalid={error ? 'true' : 'false'}
                required
              />
              {error && (
                <p className="mt-1 text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}
            </div>
            
            <div>
              <label htmlFor="categoryType" className="block text-sm font-medium text-gray-700 mb-1">
                Category Type
              </label>
              <select
                id="categoryType"
                value={newCategory.type}
                onChange={(e) => handleTypeChange(e.target.value as AccountType)}
                className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {accountTypes.map(type => (
                  <option key={type} value={type}>
                    {type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="tagsInclude" className="block text-sm font-medium text-gray-700 mb-1">
                Tags (Include)
              </label>
              <input
                id="tagsInclude"
                type="text"
                value={newCategory.tagsInclude.join(', ')}
                onChange={(e) => {
                  const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
                  setNewCategory({ ...newCategory, tagsInclude: tags });
                }}
                className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter tags separated by commas (e.g., cash, bank account, checking)"
              />
              <p className="text-xs text-gray-500 mt-1">Account names containing these words will match this category</p>
            </div>
            
            <div>
              <label htmlFor="tagsExclude" className="block text-sm font-medium text-gray-700 mb-1">
                Tags (Exclude)
              </label>
              <input
                id="tagsExclude"
                type="text"
                value={newCategory.tagsExclude.join(', ')}
                onChange={(e) => {
                  const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
                  setNewCategory({ ...newCategory, tagsExclude: tags });
                }}
                className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter tags separated by commas (e.g., over, short, loan)"
              />
              <p className="text-xs text-gray-500 mt-1">Account names containing these words will NOT match this category</p>
            </div>
            
            <div>
              <label htmlFor="nwcRole" className="block text-sm font-medium text-gray-700 mb-1">
                NWC Role
              </label>
              <NWCRoleSelector
                value={nwcRole}
                onChange={setNwcRole}
                accountType={newCategory.type}
              />
            </div>

            <div>
              <label htmlFor="ebitdaRole" className="block text-sm font-medium text-gray-700 mb-1">
                EBITDA Role
              </label>
                <EBITDARoleSelector
                  value={ebitdaRole}
                  onChange={setEbitdaRole}
                  accountType={newCategory.type}
                />
              </div>
            
            <div className="text-sm text-gray-500 italic">
              Statement type: {newCategory.fsType === 'balance-sheet' ? 'Balance Sheet' : 'Income Statement'}
            </div>

            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={newCategory.isNonCash}
                  onChange={e => setNewCategory({ ...newCategory, isNonCash: e.target.checked })}
                />
                Non-cash?
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={newCategory.isPpe}
                  onChange={e => setNewCategory({ ...newCategory, isPpe: e.target.checked })}
                />
                PPE?
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={newCategory.isDebt}
                  onChange={e => setNewCategory({ ...newCategory, isDebt: e.target.checked })}
                />
                Debt?
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={newCategory.isEquityCapital}
                  onChange={e => setNewCategory({ ...newCategory, isEquityCapital: e.target.checked })}
                />
                Equity Capital?
              </label>
            </div>
          </div>
        </form>
        
        {/* Modal Footer */}
        <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
            disabled={!newCategory.label}
          >
            Add Category
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCategoryModal;
